/**
 * Condições — os estados alterados do tabuleiro.
 *
 * O acervo traz os estados com os códigos de característica do RPG Maker, que
 * descrevem um jogo por turnos e não um tático em grade ("−10% de PV por
 * turno", "taxa de ação −100%"). Em vez de interpretar esses códigos, o motor
 * declara aqui as condições que conhece, por identificador, com semântica de
 * tabuleiro. O pipeline traduz os estados da origem para estes nomes (ver
 * `CONDITION_BY_STATE_NAME` em tools/import-mz/ozimport/config.py); o que não
 * tem tradução continua no conteúdo, sem efeito.
 *
 * **Duração.** Conta-se em turnos DA UNIDADE — as fases da equipe dela. No
 * começo da fase, a condição cobra o que tem a cobrar (veneno, queimadura) e
 * desconta um turno; no fim da fase, sai o que chegou a zero. Assim uma
 * paralisia de um turno tira exatamente uma jogada, tenha sido aplicada na
 * fase do inimigo ou num contra-ataque durante a fase da própria unidade.
 *
 * **Nenhuma condição mata sozinha.** Veneno e queimadura param em 1 PV:
 * perder um personagem sem ninguém atacar é a derrota mais frustrante que
 * existe, e a série trata igual.
 *
 * **Reaplicar renova, não empilha.** Dois venenos simultâneos dobrariam o dano
 * por turno sem que nada na tela explicasse; a duração fica com a maior.
 */
export const CONDITION_IDS = [
    'envenenado', 'queimando', 'paralisado', 'adormecido', 'cego', 'silenciado',
];
export const CONDITIONS = {
    envenenado: {
        id: 'envenenado', label: 'Envenenado', icon: 2, turns: 5,
        effects: ['dano_por_turno'], perTurn: { rate: 0.1, min: 1 },
        summary: 'Perde 10% dos PV no começo de cada turno. Lento e longo; nunca mata.',
    },
    queimando: {
        id: 'queimando', label: 'Queimando', icon: 64, turns: 2,
        effects: ['dano_por_turno'], perTurn: { rate: 0.2, min: 2 },
        summary: 'Perde 20% dos PV no começo de cada turno. Forte e curto; nunca mata.',
    },
    paralisado: {
        id: 'paralisado', label: 'Paralisado', icon: 9, turns: 1,
        effects: ['nao_age', 'sem_contra_ataque'],
        summary: 'Não move, não age e não revida.',
    },
    adormecido: {
        id: 'adormecido', label: 'Adormecido', icon: 8, turns: 2,
        effects: ['nao_age', 'sem_contra_ataque', 'acorda_ao_sofrer'],
        summary: 'Não age nem revida. Acorda ao levar dano.',
    },
    cego: {
        id: 'cego', label: 'Cego', icon: 3, turns: 2,
        effects: ['acerto_reduzido'], hitPenalty: 30,
        summary: 'Acerto −30, no ataque e no revide.',
    },
    silenciado: {
        id: 'silenciado', label: 'Silenciado', icon: 4, turns: 2,
        effects: ['sem_magia'],
        summary: 'Não ataca com arma mágica nem usa cajado. Poções e itens, sim.',
    },
};
export function isConditionId(value) {
    return typeof value === 'string' && CONDITION_IDS.includes(value);
}
export function conditionDef(id) {
    return isConditionId(id) ? CONDITIONS[id] : undefined;
}
export function hasCondition(states, id) {
    return states.some((s) => s.condition === id);
}
export function hasEffect(states, effect) {
    return states.some((s) => conditionDef(s.condition)?.effects.includes(effect) ?? false);
}
/** Quanto as condições tiram do acerto. */
export function hitPenaltyOf(states) {
    let total = 0;
    for (const state of states ?? [])
        total += conditionDef(state.condition)?.hitPenalty ?? 0;
    return total;
}
/** Os PV que uma condição de dano cobra de quem tem `maxHp`. */
export function perTurnDamage(def, maxHp) {
    if (!def.perTurn)
        return 0;
    return Math.max(def.perTurn.min, Math.floor(maxHp * def.perTurn.rate));
}
/**
 * O que o começo do turno cobra, condição por condição.
 *
 * Separado por condição, e não somado, porque a tela conta cada uma — "sofre 3
 * de veneno" diz mais do que "perde 9 PV".
 */
export function upkeepDamage(states, maxHp) {
    const out = [];
    for (const state of states) {
        const def = conditionDef(state.condition);
        if (!def)
            continue;
        const amount = perTurnDamage(def, maxHp);
        if (amount > 0)
            out.push({ condition: def.id, amount });
    }
    return out;
}
/** Começo do turno da unidade: cada condição com prazo perde um turno. */
export function beginTurn(states) {
    return states.map((s) => s.turnsLeft === null ? s : { ...s, turnsLeft: Math.max(0, s.turnsLeft - 1) });
}
/** Fim do turno da unidade: sai o que chegou a zero. */
export function endTurn(states) {
    const kept = [];
    const expired = [];
    for (const state of states) {
        if (state.turnsLeft === 0)
            expired.push(state.condition);
        else
            kept.push(state);
    }
    return { kept, expired };
}
/**
 * Aplica uma condição. Reaplicar renova: fica a duração maior, sem empilhar.
 */
export function applyCondition(states, id, turns = CONDITIONS[id].turns) {
    // `undefined` é "não tinha"; `null` é "já era até alguém curar", e continua.
    const previous = states.find((s) => s.condition === id)?.turnsLeft;
    const others = states.filter((s) => s.condition !== id);
    let renewed;
    if (previous === null || turns === null)
        renewed = null;
    else
        renewed = previous === undefined ? turns : Math.max(previous, turns);
    return [...others, { condition: id, turnsLeft: renewed }];
}
/** Tira estas condições; as demais ficam. */
export function removeConditions(states, ids) {
    return states.filter((s) => !ids.includes(s.condition));
}
/** Das condições da unidade, as que esta lista cura. */
export function curableBy(states, cures) {
    if (!cures || cures.length === 0)
        return [];
    return states.filter((s) => cures.includes(s.condition)).map((s) => s.condition);
}
/**
 * O dano acorda quem dorme.
 *
 * O sono existe para dar uma janela, não para deixar a unidade indefesa até o
 * fim da partida: o primeiro golpe que tira PV a devolve ao jogo.
 */
export function wakeOnDamage(states) {
    const kept = [];
    const woke = [];
    for (const state of states) {
        if (conditionDef(state.condition)?.effects.includes('acorda_ao_sofrer'))
            woke.push(state.condition);
        else
            kept.push(state);
    }
    return { kept, woke };
}
/**
 * As aflições de um golpe, sem repetir condição.
 *
 * A arma e a criatura podem causar a mesma coisa — uma adaga envenenada nas
 * mãos de um bicho peçonhento. Dois sorteios para o mesmo veneno só
 * inflariam a chance sem que a previsão de combate mostrasse o porquê; vale a
 * maior.
 */
export function mergeAfflictions(...lists) {
    const byCondition = new Map();
    for (const list of lists) {
        for (const affliction of list ?? []) {
            if (!isConditionId(affliction.condition) || affliction.chance <= 0)
                continue;
            const chance = Math.min(100, Math.round(affliction.chance));
            const current = byCondition.get(affliction.condition);
            if (!current || chance > current.chance) {
                byCondition.set(affliction.condition, { ...affliction, chance });
            }
        }
    }
    // Ordem estável — a do vocabulário —, porque cada aflição é um sorteio, e a
    // ordem dos sorteios decide o resultado de uma semente.
    return CONDITION_IDS.flatMap((id) => {
        const found = byCondition.get(id);
        return found ? [found] : [];
    });
}
//# sourceMappingURL=states.js.map